import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X, Trash } from "@phosphor-icons/react";

const ENTITY_TYPES = [
  { key: "case", label: "Cases" },
  { key: "client_invoice", label: "Client Invoices" },
  { key: "invoice", label: "Auto Invoices" },
  { key: "client", label: "Clients" },
  { key: "sighting", label: "Sightings" },
  { key: "supplier", label: "Suppliers" },
  { key: "user", label: "Users" },
  { key: "bd_expense", label: "BD Expenses" },
  { key: "expense_claim", label: "Expense Claims" },
  { key: "vendor", label: "Vendors" },
  { key: "bank_account", label: "Bank Accounts" },
  { key: "court_bundle", label: "Court Bundles" },
];

const FIELD_TYPES = ["text", "number", "date", "select", "boolean"];

export default function CustomFields() {
  const { user } = useAuth();
  const tier = user?.ux_tier ?? 0;
  const canCreate = tier >= 4;
  const canDelete = tier >= 5;
  const [entity, setEntity] = useState("case");
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/custom-fields/${entity}`);
      setFields(data.fields || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entity]);

  const remove = async (f) => {
    if (!canDelete) return;
    if (!window.confirm(`Remove field "${f.label}"? Existing record values will remain in the database but stop showing in the UI.`)) return;
    try {
      await api.delete(`/custom-fields/${f.field_id}`);
      await load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || "Failed."); }
  };

  return (
    <div className="space-y-6" data-testid="custom-fields-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">Schema Builder</div>
          <h1 className="font-display font-bold text-[28px]">Custom Fields</h1>
          <p className="text-[12px] text-[var(--span-text-2)] mt-1 max-w-[640px]">
            Extend any record type with extra columns. COO/CEO can add fields; only the
            CEO can remove them. Field values are stored under <span className="font-mono">custom.&lt;key&gt;</span>
            {" "}on each record.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={entity} onChange={(e) => setEntity(e.target.value)} data-testid="entity-filter" className="span-input h-9 text-[12px]">
            {ENTITY_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          {canCreate && (
            <button onClick={() => setOpen(true)} data-testid="open-new-field" className="span-btn h-9 text-[12px]">
              <Plus size={13} /> New field
            </button>
          )}
        </div>
      </div>

      {err && <div className="card-flat p-4 text-[12px] text-[var(--span-danger)]">{err}</div>}

      <div className="card-flat overflow-hidden">
        <table className="span-table" data-testid="fields-table">
          <thead>
            <tr>
              <th>Label</th><th>Key</th><th>Type</th><th>Options</th><th>Required</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-8 text-[var(--span-text-2)] font-mono text-[11px]">LOADING…</td></tr>}
            {!loading && fields.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-[var(--span-text-2)] text-[12px]">No custom fields defined for this entity.</td></tr>
            )}
            {fields.map(f => (
              <tr key={f.field_id} data-testid={`field-row-${f.field_key}`}>
                <td className="font-medium">{f.label}</td>
                <td className="font-mono text-[12px] text-[var(--span-text-2)]">{f.field_key}</td>
                <td><span className="badge">{f.type}</span></td>
                <td className="text-[12px] text-[var(--span-text-2)]">{(f.options || []).join(", ") || "—"}</td>
                <td>{f.required ? <span className="badge badge-warn">required</span> : "—"}</td>
                <td className="text-right">
                  {canDelete && (
                    <button onClick={() => remove(f)} data-testid={`remove-${f.field_key}`} className="span-btn span-btn-ghost h-7 text-[10px]" style={{ color: "var(--span-danger)" }}>
                      <Trash size={11} /> Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <NewFieldModal entity={entity} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function NewFieldModal({ entity, onClose, onCreated }) {
  const [form, setForm] = useState({ field_key: "", label: "", type: "text", options: "", required: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {
        entity_type: entity,
        field_key: form.field_key.trim(),
        label: form.label.trim(),
        type: form.type,
        required: !!form.required,
      };
      if (form.type === "select") payload.options = form.options.split(",").map(s => s.trim()).filter(Boolean);
      await api.post("/custom-fields", payload);
      onCreated();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="card-flat w-full max-w-[520px]">
        <div className="px-5 py-3.5 border-b border-[var(--span-border)] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">New custom field</div>
            <div className="font-display font-bold text-[16px]">{entity}</div>
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1.5">Key (lowercase, no spaces)</label>
              <input data-testid="field-key" required value={form.field_key} onChange={(e) => setForm({ ...form, field_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} className="span-input font-mono text-[12px]" placeholder="brand_owner" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Label</label>
              <input data-testid="field-label" required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="span-input" placeholder="Brand Owner" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1.5">Type</label>
              <select data-testid="field-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="span-input">
                {FIELD_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 mt-6 cursor-pointer">
              <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />
              <span className="text-[12px]">Required field</span>
            </label>
          </div>
          {form.type === "select" && (
            <div>
              <label className="label-eyebrow block mb-1.5">Options (comma-separated)</label>
              <input data-testid="field-options" value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} className="span-input font-mono text-[12px]" placeholder="Option A, Option B, Option C" />
            </div>
          )}
          {err && <div className="text-[12px] text-[var(--span-danger)]">{err}</div>}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
            <button data-testid="submit-field" disabled={busy} className="span-btn disabled:opacity-50">{busy ? "Saving…" : "Create field"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
